import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * ScrollHint — wraps a horizontally-scrollable child (typically a table) and adds:
 *   • a right-edge gradient fade to indicate more content exists
 *   • a small "swipe" chevron hint at the top-right corner
 * Both overlays use `pointer-events: none` so they never intercept clicks and
 * NEVER hide any table content — they sit above the container edge only when
 * more content exists to the right.
 *
 * The hint auto-hides when the child is scrolled to the far right, or when the
 * child fits without overflow (no scroll possible).
 *
 * Usage:
 *   <ScrollHint>
 *     <div className="overflow-x-auto ..."><table>...</table></div>
 *   </ScrollHint>
 */
export default function ScrollHint({ children, className = '', testId = 'scroll-hint' }) {
  const wrapRef = useRef(null);
  const [state, setState] = useState({ canScroll: false, atEnd: false });

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    // The direct child is expected to be the scrollable container.
    const scroller = wrap.querySelector('[data-scroll-host]') || wrap.firstElementChild;
    if (!scroller) return;

    const update = () => {
      const canScroll = scroller.scrollWidth > scroller.clientWidth + 2;
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      setState((s) => (s.canScroll === canScroll && s.atEnd === atEnd ? s : { canScroll, atEnd }));
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    // Recompute on window resize (viewport change) and content mutations (async row loads).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) { ro.observe(scroller); if (scroller.firstElementChild) ro.observe(scroller.firstElementChild); }
    window.addEventListener('resize', update);

    return () => {
      scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [children]);

  const show = state.canScroll && !state.atEnd;

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      {children}
      {/* Right-edge gradient fade — sits ABOVE the scroll container's right
          edge, transparent → subtle bg, so text underneath is still readable.
          `pointer-events-none` guarantees no click interception. */}
      <div
        aria-hidden="true"
        data-testid={`${testId}-fade`}
        className={`pointer-events-none absolute top-0 right-0 h-full w-12 rounded-r-lg transition-opacity duration-200 bg-gradient-to-l from-white dark:from-[#111114] via-white/70 dark:via-[#111114]/70 to-transparent ${show ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* Small "→" hint chip — floats over the fade so mobile users see there's more.
          Positioned bottom-right so it doesn't overlap the table header. */}
      <div
        aria-hidden="true"
        data-testid={`${testId}-chip`}
        className={`pointer-events-none absolute bottom-2 right-2 grid place-items-center h-6 w-6 rounded-full bg-slate-900/80 dark:bg-white/85 text-white dark:text-black shadow-md transition-opacity duration-200 ${show ? 'opacity-100 animate-pulse' : 'opacity-0'}`}
      >
        <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.5} />
      </div>
    </div>
  );
}
